import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import FormError from '../../components/FormError';

type Settings = {
  company_legal_name: string; cnpj: string | null; company_email: string | null;
  company_whatsapp: string | null; address: string | null;
  responsible_name: string; responsible_role: string; responsible_email: string | null;
  lgpd_text: string | null; logo_path: string | null; updated_at: string;
  ai_base_url: string; ai_model: string; ai_api_key_masked: string | null;
  serp_api_key_masked: string | null;
  evolution_url: string | null; evolution_api_key_masked: string | null; evolution_instance: string;
  smtp_host: string | null; smtp_port: number; smtp_user: string | null; smtp_from: string | null; smtp_pass_masked: string | null;
};

const FIELDS: { key: keyof Settings; label: string; hint?: string; wide?: boolean; full?: boolean }[] = [
  { key: 'company_legal_name', label: 'Razão social / nome comercial' },
  { key: 'cnpj', label: 'CNPJ da empresa', hint: ' aparece no rodapé da landing e em propostas' },
  { key: 'company_email', label: 'Email da empresa' },
  { key: 'company_whatsapp', label: 'WhatsApp da empresa' },
  { key: 'address', label: 'Endereço (cidade/UF basta)' },
  { key: 'responsible_name', label: 'Responsável legal — nome' },
  { key: 'responsible_role', label: 'Responsável legal — cargo' },
  { key: 'responsible_email', label: 'Responsável legal — email' },
];

export default function Configuracoes() {
  const [s, setS] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const d = await api.get<{ settings: Settings | null }>('/settings');
    setS(d.settings);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function onSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    setFormError(null);
    try {
      await api.patch('/settings', Object.fromEntries(fd.entries()));
      toast.success('Salvo. As informações circulam pela plataforma imediatamente.');
      await load();
    } catch (err) {
      const e = err as { message?: string; details?: Record<string, string[]> };
      const first = e.details ? Object.entries(e.details)[0] : null;
      setFormError(first ? `${first[0]}: ${first[1][0]}` : e.message ?? 'Falha ao salvar');
    } finally {
      setBusy(false);
    }
  }

  async function onLogo(file: File | null) {
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file, file.name);
    setFormError(null);
    try {
      await api.multipart('/settings/logo', fd);
      toast.success('Logo upload OK — no ar na landing e no painel.');
      await load();
    } catch (err) {
      const e = err as { message?: string; details?: Record<string, string[]> };
      const first = e.details ? Object.entries(e.details)[0] : null;
      setFormError(first ? `${first[0]}: ${first[1][0]}` : e.message ?? 'Falha no upload do logo');
    }
  }

  return (
    <div className="max-w-3xl">
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Admin</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Configurações da empresa</h1>
        <p className="mt-1 text-sm text-[var(--text-dim)]">
          Preencha aqui os dados da empresa e do responsável legal: o que você salvar circula automaticamente na landing, painel, propostas e contatos.
        </p>
      </header>
      {s && (
        <form onSubmit={onSave} className="mt-5 space-y-4 rounded-lg border border-[var(--border)] bg-white p-4">
          <section aria-label="Integrações" className="rounded-lg border border-[var(--border)] p-4">
            <header className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold">Integrações (APIs)</h2>
                <p className="mt-0.5 text-xs text-[var(--text-dim)]">Igual ao padrão de integrações do demo astral: a chave viva no banco circula instantanemente. Deixe em branco para manter a atual. Até configurar, tudo roda em modo demo.</p>
              </div>
            </header>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { key: 'ai_api_key', label: 'Chave da IA (Gemini via OpenAI-compatível)', masked: s.ai_api_key_masked },
                { key: 'serp_api_key', label: 'Chave da API — SerpAPI (prospecção)', masked: s.serp_api_key_masked },
                { key: 'evolution_api_key', label: 'Chave da API — Evolution (WhatsApp)', masked: s.evolution_api_key_masked },
                { key: 'smtp_pass', label: 'Senha da SMTP', masked: s.smtp_pass_masked },
              ].map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-sm font-medium">{f.label}</span>
                  <input
                    name={f.key}
                    type="password" autoComplete="off"
                    placeholder={f.masked ? f.masked : 'Não configurada'}
                    className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm font-mono text-xs"
                  />
                </label>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {[
                { key: 'evolution_url', label: 'Evolution — URL' },
                { key: 'evolution_instance', label: 'Evolution — instância' },
                { key: 'ai_base_url', label: 'IA — BASE_URL (OpenAI-compatível)' },
                { key: 'ai_model', label: 'IA — modelo' },
              ].map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-sm font-medium">{f.label}</span>
                  <input name={f.key} defaultValue={(s[f.key as keyof Settings] as string | null) ?? ""}
                    readOnly={false}
                    className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
                </label>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {[
                { key: 'smtp_host', label: 'SMTP — host' },
                { key: 'smtp_port', label: 'SMTP — porta' },
                { key: 'smtp_user', label: 'SMTP — usuário' },
                { key: 'smtp_from', label: 'SMTP — remetente (de:)' },
              ].map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-sm font-medium">{f.label}</span>
                  <input name={f.key} defaultValue={(s[f.key as keyof Settings] as string | number | null) ?? ""}
                    className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
                </label>
              ))}
            </div>
          </section>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <label key={f.key} className={f.wide ? 'block sm:col-span-1' : 'block'}>
                <span className="mb-1 block text-sm font-medium">{f.label}</span>
                <input
                  key={`${f.key}-${s[f.key] ?? ''}`}
                  name={f.key}
                  defaultValue={(s[f.key] as string | null) ?? ''}
                  className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm focus:outline-none"
                />
              </label>
            ))}
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Texto LGPD (aparece na landing)</span>
            <textarea
              key={`lgpd-${(s.lgpd_text ?? '').length}`}
              name="lgpd_text" rows={2}
              defaultValue={s.lgpd_text ?? ''}
              className="w-full rounded-lg border border-[var(--border-strong)] bg-white p-3 text-sm focus:outline-none"
            />
          </label>
      {s.logo_path ? (
            <div className="flex items-center gap-3">
              <img src={s.logo_path} alt="Logo da empresa" className="h-10 w-auto rounded border border-[var(--border)]" />
              <label className="h-9 cursor-pointer rounded-lg border border-[var(--border-strong)] px-3 text-sm font-medium hover:bg-[var(--bg-subtle)]">
                Trocar logo
                <input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={(e) => onLogo(e.target.files?.[0] ?? null)} />
              </label>
            </div>
          ) : (
            <label className="inline-flex h-9 cursor-pointer items-center rounded-lg border border-[var(--border-strong)] px-3 text-sm font-medium hover:bg-[var(--bg-subtle)]">
              Envie o logo (PNG/JPG/SVG/WEBP, até 3MB)
              <input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={(e) => onLogo(e.target.files?.[0] ?? null)} />
            </label>
          )}
          <div className="flex items-center justify-between gap-3 border-t border-[var(--border)] pt-3">
            <span className="text-xs text-[var(--text-dim)]">Última atualização: {new Date(s.updated_at).toLocaleString('pt-BR')}</span>
            <button type="submit" disabled={busy} className="h-9 rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
              {busy ? 'Salvando...' : 'Salvar e circular'}
            </button>
          </div>
          <FormError error={formError} onClear={() => setFormError(null)} />
        </form>
      )}
    </div>
  );
}
